"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Check } from "lucide-react";

type ProgressStep = {
  id: string;
  label: string;
  shortLabel?: string;
};

type ProgressBarProps = {
  steps: ProgressStep[];
  currentStep: number;
  className?: string;
  onStepClick?: (index: number) => void;
  allowClickBack?: boolean;
};

function ProgressBar({
  steps,
  currentStep,
  className,
  onStepClick,
  allowClickBack = true,
}: ProgressBarProps) {
  return (
    <div className={cn("w-full", className)}>
      {/* Mobile: Compact progress */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            Step {currentStep + 1} of {steps.length}
          </span>
          <span className="text-sm text-muted-foreground">
            {steps[currentStep]?.label}
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300 ease-out rounded-full"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Desktop: Full step indicator */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;
            const isClickable = allowClickBack && isCompleted && onStepClick;

            return (
              <React.Fragment key={step.id}>
                {/* Step circle and label */}
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={() => isClickable && onStepClick(index)}
                    disabled={!isClickable}
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200",
                      isCompleted &&
                        "bg-primary text-primary-foreground cursor-pointer hover:bg-primary/90",
                      isCurrent &&
                        "bg-primary text-primary-foreground ring-4 ring-primary/20",
                      !isCompleted &&
                        !isCurrent &&
                        "bg-muted text-muted-foreground",
                      isClickable && "cursor-pointer",
                      !isClickable && "cursor-default",
                    )}
                    aria-label={`Step ${index + 1}: ${step.label}`}
                    aria-current={isCurrent ? "step" : undefined}
                  >
                    {isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </button>
                  <span
                    className={cn(
                      "mt-2 text-xs font-medium text-center max-w-[80px]",
                      isCurrent && "text-foreground",
                      isCompleted && "text-primary",
                      !isCompleted && !isCurrent && "text-muted-foreground/70",
                    )}
                  >
                    {step.shortLabel || step.label}
                  </span>
                </div>

                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="flex-1 mx-2 h-1 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full bg-primary transition-all duration-300",
                        index < currentStep ? "w-full" : "w-0",
                      )}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { ProgressBar };
export type { ProgressStep, ProgressBarProps };
